import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { IntellioAssistantWorkItemRecord } from '@triedge/domain-core';
import {
  buildExplicitPromptBindingFromAssistantPrompt,
  validateExplicitPromptBindingForRoute,
} from './assistantPromptBinding';

const SESSION_STORAGE_KEY = 'ystnj_ai_hidden_proactive_work_ids';
const ACTIVE_WORK_STATES = new Set(['queued', 'snoozed']);

const normalizeText = (value: unknown): string => String(value ?? '').trim();

const readHiddenIds = (): string[] => {
  if (typeof window === 'undefined') {
    return [];
  }
  try {
    const raw = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed)
      ? parsed.map((value) => normalizeText(value)).filter((value) => value.length > 0)
      : [];
  } catch {
    return [];
  }
};

const writeHiddenIds = (ids: readonly string[]) => {
  if (typeof window === 'undefined') {
    return;
  }
  window.sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(ids));
};

const topLevelRouteKey = (pathname: string): string => {
  const parts = pathname.split('/').filter(Boolean);
  return parts.slice(0, 3).join('/');
};

const scoreUrgency = (urgency: string): number => {
  switch (normalizeText(urgency).toLowerCase()) {
    case 'critical':
      return 40;
    case 'high':
      return 30;
    case 'normal':
      return 20;
    case 'low':
      return 10;
    default:
      return 0;
  }
};

export const scoreProactiveWorkItemRouteRelevance = (
  pathname: string,
  route: string,
): number => {
  const current = normalizeText(pathname);
  const target = normalizeText(route);
  if (!current || !target) {
    return 0;
  }
  if (current === target) {
    return 300;
  }
  if (current.startsWith(target) || target.startsWith(current)) {
    return 250;
  }
  if (topLevelRouteKey(current) === topLevelRouteKey(target)) {
    return 200;
  }
  const currentModule = current.split('/').filter(Boolean).slice(0, 2).join('/');
  const targetModule = target.split('/').filter(Boolean).slice(0, 2).join('/');
  if (currentModule && currentModule === targetModule) {
    return 150;
  }
  return 0;
};

export const selectPreferredProactiveWorkItem = (
  items: readonly IntellioAssistantWorkItemRecord[],
  pathname: string,
  hiddenIds: ReadonlySet<string>,
  persona?: string | null,
): IntellioAssistantWorkItemRecord | null => {
  const candidates = items
    .filter((item) => {
      const id = normalizeText(item.id);
      if (!id || hiddenIds.has(id)) {
        return false;
      }
      if (!item.assistantPrompt) {
        return false;
      }
      const promptBinding = buildExplicitPromptBindingFromAssistantPrompt(item.assistantPrompt);
      if (promptBinding) {
        const validation = validateExplicitPromptBindingForRoute({
          binding: promptBinding,
          pathname: normalizeText(item.route),
          persona: persona ?? normalizeText(item.actorRole),
        });
        if (!validation.isValid) {
          return false;
        }
      }
      return ACTIVE_WORK_STATES.has(normalizeText(item.state).toLowerCase());
    })
    .map((item) => ({
      item,
      routeScore: scoreProactiveWorkItemRouteRelevance(pathname, normalizeText(item.route)),
      urgencyScore: scoreUrgency(item.urgency),
      updatedAt: Number(item.updatedAt ?? 0),
    }))
    .filter((candidate) => candidate.routeScore > 0);

  if (candidates.length === 0) {
    return null;
  }

  candidates.sort((left, right) => (
    right.routeScore - left.routeScore
    || right.urgencyScore - left.urgencyScore
    || right.updatedAt - left.updatedAt
  ));

  return candidates[0]?.item ?? null;
};

export const useProactiveTrayState = (input: {
  items: readonly IntellioAssistantWorkItemRecord[];
  pathname: string;
  persona?: string | null;
}) => {
  const [hiddenIds, setHiddenIds] = useState<string[]>(() => readHiddenIds());
  const [collapsedIds, setCollapsedIds] = useState<Record<string, true>>({});
  const [suppressedPathname, setSuppressedPathname] = useState<string | null>(null);
  const seenIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (suppressedPathname && suppressedPathname !== input.pathname) {
      setSuppressedPathname(null);
    }
  }, [input.pathname, suppressedPathname]);

  useEffect(() => {
    writeHiddenIds(hiddenIds);
  }, [hiddenIds]);

  const hiddenIdSet = useMemo(() => new Set(hiddenIds), [hiddenIds]);
  const activeEntry = useMemo(
    () => (
      suppressedPathname === input.pathname
        ? null
        : selectPreferredProactiveWorkItem(input.items, input.pathname, hiddenIdSet, input.persona)
    ),
    [hiddenIdSet, input.items, input.pathname, input.persona, suppressedPathname]
  );

  useEffect(() => {
    if (!activeEntry) {
      return;
    }
    if (!seenIdsRef.current.has(activeEntry.id)) {
      seenIdsRef.current.add(activeEntry.id);
      setCollapsedIds((previous) => {
        if (!previous[activeEntry.id]) {
          return previous;
        }
        const next = { ...previous };
        delete next[activeEntry.id];
        return next;
      });
    }
  }, [activeEntry]);

  const hideWorkItem = useCallback((workItemId: string) => {
    const id = normalizeText(workItemId);
    if (!id) {
      return () => undefined;
    }
    setHiddenIds((previous) => (previous.includes(id) ? previous : [...previous, id]));
    return () => {
      setHiddenIds((previous) => previous.filter((entry) => entry !== id));
    };
  }, []);

  const toggleCollapsed = useCallback((workItemId: string) => {
    const id = normalizeText(workItemId);
    if (!id) {
      return;
    }
    setCollapsedIds((previous) => {
      if (previous[id]) {
        const next = { ...previous };
        delete next[id];
        return next;
      }
      return {
        ...previous,
        [id]: true,
      };
    });
  }, []);

  const executeOptimisticHide = useCallback(async (
    workItemId: string,
    task: () => Promise<void>,
  ) => {
    const rollback = hideWorkItem(workItemId);
    const previousSuppressedPath = suppressedPathname;
    setSuppressedPathname(input.pathname);
    try {
      await task();
    } catch (error) {
      rollback();
      setSuppressedPathname(previousSuppressedPath);
      throw error;
    }
  }, [hideWorkItem, input.pathname, suppressedPathname]);

  return {
    activeEntry,
    isCollapsed: activeEntry ? Boolean(collapsedIds[activeEntry.id]) : false,
    toggleCollapsed,
    hideWorkItem,
    executeOptimisticHide,
  };
};
